import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../lib/api'
import {
  Building,
  MessageSquare,
  Phone,
  Users,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  AlertTriangle,
  Wallet
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { format } from 'date-fns'
import CostTrendChart from '@/components/admin/CostTrendChart'
import CostBreakdownChart from '@/components/admin/CostBreakdownChart'
import StripeBackfillCard from '@/components/admin/StripeBackfillCard'
import { useAuth } from '@/hooks/useAuth'
import { TierBadge } from '@/components/ui/TierBadge'

interface PlatformAnalytics {
  organizations: {
    total: number
    active: number
    suspended: number
    pending: number
    byTier: {
      trial: number
      starter: number
      professional: number
      enterprise: number
    }
    newThisMonth: number
    newLastMonth: number
    growthRate: string
  }
  agents: {
    total: number
    online: number
  }
  conversations: {
    total: number
    thisMonth: number
    lastMonth: number
    thisWeek: number
    growthRate: string
  }
  voiceCalls: {
    total: number
    thisMonth: number
    lastMonth: number
    minutesThisMonth: number
    minutesLastMonth: number
  }
  messages: {
    total: number
    thisMonth: number
    byType: {
      visitor: number
      ai: number
      agent: number
    }
  }
  costs: {
    voiceCostThisMonth: number
    phoneCostThisMonth: number
    breakdown: {
      transport: number
      stt: number
      llm: number
      tts: number
    }
  }
  revenue: {
    mrrEstimate: string
    newSubscriptionsThisMonth: number
  }
  conversationTrend: Array<{
    date: string
    count: number
  }>
  voiceTrend: Array<{
    date: string
    calls: number
    minutes: number
    cost: number
  }>
  topOrganizations: Array<{
    id: string
    name: string
    slug: string
    subscriptionTier: string
    conversationCount: number
    messageCount: number
    voiceCost: number
  }>
  recentSignups: Array<{
    id: string
    name: string
    slug: string
    subscriptionTier: string
    status: string
    ownerEmail: string
    createdAt: string
  }>
}

interface ConcurrencyStatus {
  activeCount: number
  effectiveLimit: number
  baseLimit: number
  burstEnabled: boolean
  utilization: number
  queueDepth: number
}

interface TelnyxBalance {
  balance: number
  currency: string
  creditLimit: number
  availableCredit: number
}

interface OpenAIStatus {
  status: 'operational' | 'quota_exceeded' | 'auth_error' | 'rate_limited' | 'unavailable' | 'not_configured'
  detail?: string
  checkedAt: string
}

interface ElevenLabsStatus {
  status: 'operational' | 'quota_low' | 'quota_exhausted' | 'auth_error' | 'unavailable' | 'not_configured'
  detail?: string
  used?: number
  limit?: number
  remaining?: number
  pctUsed?: number
  tier?: string | null
  resetAt?: string | null
  checkedAt: string
}

// Warning thresholds (USD). If Telnyx charges for numbers at ~$1–2 each plus
// per-minute usage, $50 is the "nudge James to top up" line and $10 is the
// "you will be blocked from provisioning" emergency line.
const TELNYX_LOW_THRESHOLD = 50
const TELNYX_CRITICAL_THRESHOLD = 10

export default function PlatformDashboard() {
  const { agent } = useAuth()
  const isSuperAdmin = Boolean((agent as any)?.isSuperAdmin)
  const [analytics, setAnalytics] = useState<PlatformAnalytics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [concurrency, setConcurrency] = useState<ConcurrencyStatus | null>(null)
  const [concurrencyStale, setConcurrencyStale] = useState(false)
  const [telnyxBalance, setTelnyxBalance] = useState<TelnyxBalance | null>(null)
  const [telnyxBalanceUnavailable, setTelnyxBalanceUnavailable] = useState(false)
  const [openaiStatus, setOpenaiStatus] = useState<OpenAIStatus | null>(null)
  const [openaiRechecking, setOpenaiRechecking] = useState(false)
  const [elevenlabsStatus, setElevenlabsStatus] = useState<ElevenLabsStatus | null>(null)
  const [elevenlabsRechecking, setElevenlabsRechecking] = useState(false)
  const alertedAtHighRef = useState<{ current: boolean }>({ current: false })[0]
  const alertedLowBalanceRef = useState<{ current: boolean }>({ current: false })[0]
  const alertedOpenaiRef = useState<{ current: boolean }>({ current: false })[0]
  const alertedElevenlabsRef = useState<{ current: boolean }>({ current: false })[0]

  useEffect(() => {
    loadAnalytics()
  }, [])

  // Live concurrency poll — refresh every 15s so the live bar stays current
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.get('/api/voice/concurrency')
        if (!cancelled && res.data.success) {
          setConcurrency({
            activeCount: res.data.activeCount,
            effectiveLimit: res.data.effectiveLimit,
            baseLimit: res.data.baseLimit,
            burstEnabled: res.data.burstEnabled,
            utilization: res.data.utilization,
            queueDepth: res.data.queueDepth,
          })
          setConcurrencyStale(false)

          // Browser-notify when platform utilization first crosses 90%.
          // Resets when it drops below 70%, so we don't spam on bouncy loads.
          if (res.data.utilization >= 90 && !alertedAtHighRef.current) {
            alertedAtHighRef.current = true
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Platform at capacity', {
                body: `${res.data.activeCount}/${res.data.effectiveLimit} concurrent calls (${res.data.utilization}%). Additional callers will hit voicemail.`,
                tag: 'platform-capacity',
                requireInteraction: true,
              })
            }
          } else if (res.data.utilization < 70 && alertedAtHighRef.current) {
            alertedAtHighRef.current = false
          }
        }
      } catch {
        // Keep the last known counts on screen but flag them stale so the
        // super admin knows the live number may be out of date.
        if (!cancelled) setConcurrencyStale(true)
      }
    }
    load()
    const id = setInterval(load, 15_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  // Telnyx balance — poll every 5 min so super admins know before credit runs
  // out. Silent failure (card shows "unavailable" rather than toasting).
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.get('/api/platform/telnyx-balance')
        if (cancelled) return
        if (res.data?.success && res.data.balance) {
          setTelnyxBalance(res.data.balance)
          setTelnyxBalanceUnavailable(false)

          // Browser-notify the first time balance drops below the low
          // threshold. Resets when it goes back above the threshold so a
          // top-up brings the alert back online.
          const available = res.data.balance.availableCredit as number
          if (available < TELNYX_LOW_THRESHOLD && !alertedLowBalanceRef.current) {
            alertedLowBalanceRef.current = true
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Telnyx credit low', {
                body: `Available credit is $${available.toFixed(2)}. Top up before provisioning more numbers.`,
                tag: 'telnyx-balance',
                requireInteraction: available < TELNYX_CRITICAL_THRESHOLD,
              })
            }
          } else if (available >= TELNYX_LOW_THRESHOLD && alertedLowBalanceRef.current) {
            alertedLowBalanceRef.current = false
          }
        } else {
          setTelnyxBalanceUnavailable(true)
        }
      } catch {
        if (!cancelled) setTelnyxBalanceUnavailable(true)
      }
    }
    load()
    const id = setInterval(load, 5 * 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  // OpenAI health — cached server-side for 12h, polled every 12h here. The
  // probe burns a couple of tokens per call (fractions of a cent), but James
  // called the 2-minute cadence "way too aggressive" — quota state doesn't
  // move fast enough to justify that, so we're deliberately conservative.
  // If you top up and want to verify immediately, reload the page after the
  // server cache expires (or wait for the next auto-poll).
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.get('/api/platform/openai-status')
        if (cancelled) return
        if (res.data?.success) {
          setOpenaiStatus(res.data)
          const s = res.data.status
          const isDown = s === 'quota_exceeded' || s === 'auth_error'
          if (isDown && !alertedOpenaiRef.current) {
            alertedOpenaiRef.current = true
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('OpenAI unavailable', {
                body: s === 'quota_exceeded'
                  ? 'OpenAI quota exhausted — chat lead extraction and voice booking date detection are degraded. Top up now.'
                  : 'OpenAI API key rejected — check OPENAI_API_KEY.',
                tag: 'openai-status',
                requireInteraction: true,
              })
            }
          } else if (!isDown && alertedOpenaiRef.current) {
            alertedOpenaiRef.current = false
          }
        }
      } catch {
        // Swallow — card will just stay on last known status.
      }
    }
    load()
    const id = setInterval(load, 12 * 60 * 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  // Manual recheck for OpenAI status — bypasses the server cache so the
  // super admin can verify a top-up took effect without waiting 12h.
  const recheckOpenAI = async () => {
    setOpenaiRechecking(true)
    try {
      const res = await api.get('/api/platform/openai-status?fresh=1')
      if (res.data?.success) setOpenaiStatus(res.data)
    } catch {
      // Leave the existing card state untouched on network failure.
    } finally {
      setOpenaiRechecking(false)
    }
  }

  // ElevenLabs character usage — ElevenLabs has a real quota endpoint so we
  // show actual used/limit numbers. Poll every 12h (cached server-side 12h
  // too); character counts don't move fast enough to justify more. Use the
  // Recheck button on the card for an immediate refresh after plan changes.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.get('/api/platform/elevenlabs-status')
        if (cancelled) return
        if (res.data?.success) {
          setElevenlabsStatus(res.data)
          const s = res.data.status
          const isDown = s === 'quota_exhausted' || s === 'auth_error'
          if (isDown && !alertedElevenlabsRef.current) {
            alertedElevenlabsRef.current = true
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('ElevenLabs unavailable', {
                body: s === 'quota_exhausted'
                  ? 'ElevenLabs character quota exhausted — voice calls will fail. Top up or upgrade the plan.'
                  : 'ElevenLabs API key rejected — check ELEVENLABS_API_KEY.',
                tag: 'elevenlabs-status',
                requireInteraction: true,
              })
            }
          } else if (!isDown && alertedElevenlabsRef.current) {
            alertedElevenlabsRef.current = false
          }
        }
      } catch {
        // Swallow — card holds last known status.
      }
    }
    load()
    const id = setInterval(load, 12 * 60 * 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  // Manual recheck for ElevenLabs — bypasses the 12h cache so super admins
  // can verify a plan upgrade landed without waiting.
  const recheckElevenLabs = async () => {
    setElevenlabsRechecking(true)
    try {
      const res = await api.get('/api/platform/elevenlabs-status?fresh=1')
      if (res.data?.success) setElevenlabsStatus(res.data)
    } catch {
      // Leave the existing card state untouched on network failure.
    } finally {
      setElevenlabsRechecking(false)
    }
  }

  const loadAnalytics = async () => {
    try {
      const response = await api.get('/api/platform/analytics')
      if (response.data.success) {
        setAnalytics(response.data.analytics)
      }
    } catch (error) {
      console.error('Failed to load platform analytics:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getTierBadge = (tier: string) => <TierBadge tier={tier} />


  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default',
      pending: 'secondary',
      suspended: 'destructive'
    }
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>
  }

  const formatGrowth = (rate: string | number) => {
    if (rate === 'N/A') return null
    const num = parseFloat(rate as string)
    const isPositive = num >= 0
    return (
      <span className={`flex items-center text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
        {isPositive ? '+' : ''}{rate}%
      </span>
    )
  }

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-28 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-96 bg-muted animate-pulse rounded-lg" />
          <div className="h-96 bg-muted animate-pulse rounded-lg" />
        </div>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Failed to load platform analytics</p>
      </div>
    )
  }

  const conversationTrendFormatted = analytics.conversationTrend.map(item => ({
    ...item,
    displayDate: format(new Date(item.date), 'MMM dd')
  }))

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="The Castle"
        title="Platform Dashboard"
        subtitle="Command center for all organizations and platform-wide metrics."
      />

      {/* Telnyx balance — warns before credit runs out and number provisioning fails */}
      {telnyxBalance && (() => {
        const available = telnyxBalance.availableCredit
        const isCritical = available < TELNYX_CRITICAL_THRESHOLD
        const isLow = available < TELNYX_LOW_THRESHOLD
        const tone = isCritical
          ? { border: 'border-red-500/60', icon: 'text-red-500', text: 'text-red-600', badge: 'Critical' }
          : isLow
          ? { border: 'border-yellow-500/60', icon: 'text-yellow-500', text: 'text-yellow-600', badge: 'Low' }
          : { border: '', icon: 'text-green-500', text: 'text-muted-foreground', badge: null }
        return (
          <Card className={tone.border}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div className="flex items-center gap-2">
                <Wallet className={`h-5 w-5 ${tone.icon}`} />
                <CardTitle className="text-base">Telnyx account credit</CardTitle>
              </div>
              <div className="text-sm font-mono font-medium">
                ${available.toFixed(2)} {telnyxBalance.currency}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center justify-between">
                <p className={`text-xs ${tone.text}`}>
                  {isCritical
                    ? 'Top up now — number provisioning will fail at zero.'
                    : isLow
                    ? 'Credit is running low. Top up before buying more numbers.'
                    : `Healthy. Balance $${telnyxBalance.balance.toFixed(2)}${telnyxBalance.creditLimit > 0 ? ` + $${telnyxBalance.creditLimit.toFixed(2)} credit line` : ''}.`}
                </p>
                <div className="flex items-center gap-3">
                  {tone.badge && (
                    <div className={`flex items-center gap-1 text-xs ${tone.text}`}>
                      <AlertTriangle className="h-3 w-3" />
                      <span>{tone.badge}</span>
                    </div>
                  )}
                  <a
                    href="https://portal.telnyx.com/#/app/billing/payment-methods"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs underline text-muted-foreground hover:text-foreground"
                  >
                    Top up in Telnyx portal
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })()}
      {!telnyxBalance && telnyxBalanceUnavailable && (
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base text-muted-foreground">Telnyx account credit</CardTitle>
            </div>
            <div className="text-xs text-muted-foreground">
              Balance lookup unavailable
            </div>
          </CardHeader>
        </Card>
      )}

      {/* OpenAI status — quota exhaustion silently breaks chat lead extraction
          and voice booking date detection, so surface it before the symptoms. */}
      {openaiStatus && openaiStatus.status !== 'not_configured' && (() => {
        const s = openaiStatus.status
        const isDown = s === 'quota_exceeded' || s === 'auth_error'
        const isWarn = s === 'rate_limited' || s === 'unavailable'
        const tone = isDown
          ? { border: 'border-red-500/60', icon: 'text-red-500', text: 'text-red-600', label: s === 'quota_exceeded' ? 'Quota exhausted' : 'Auth error', labelColor: 'text-red-600' }
          : isWarn
          ? { border: 'border-yellow-500/60', icon: 'text-yellow-500', text: 'text-yellow-600', label: s === 'rate_limited' ? 'Rate limited' : 'Unavailable', labelColor: 'text-yellow-600' }
          : { border: '', icon: 'text-green-500', text: 'text-muted-foreground', label: 'Operational', labelColor: 'text-green-600' }
        return (
          <Card className={tone.border}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div className="flex items-center gap-2">
                <Wallet className={`h-5 w-5 ${tone.icon}`} />
                <CardTitle className="text-base">OpenAI API</CardTitle>
              </div>
              <div className={`text-sm font-medium ${tone.labelColor}`}>
                {tone.label}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center justify-between gap-4">
                <p className={`text-xs ${tone.text}`}>
                  {isDown
                    ? openaiStatus.detail || 'Chat lead extraction and voice booking detection are degraded.'
                    : isWarn
                    ? openaiStatus.detail || 'Intermittent — usually self-heals.'
                    : 'Chat extraction, call analysis, and date parsing are running cleanly.'}
                </p>
                <div className="flex items-center gap-3 shrink-0">
                  {(isDown || isWarn) && (
                    <div className={`flex items-center gap-1 text-xs ${tone.text}`}>
                      <AlertTriangle className="h-3 w-3" />
                      <span>{tone.label}</span>
                    </div>
                  )}
                  <button
                    onClick={recheckOpenAI}
                    disabled={openaiRechecking}
                    className="text-xs underline text-muted-foreground hover:text-foreground whitespace-nowrap disabled:opacity-50"
                  >
                    {openaiRechecking ? 'Checking…' : 'Recheck'}
                  </button>
                  <a
                    href="https://platform.openai.com/account/billing/overview"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs underline text-muted-foreground hover:text-foreground whitespace-nowrap"
                  >
                    View balance
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })()}
      {openaiStatus?.status === 'not_configured' && (
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base text-muted-foreground">OpenAI API</CardTitle>
            </div>
            <div className="text-xs text-muted-foreground">
              OPENAI_API_KEY not set
            </div>
          </CardHeader>
        </Card>
      )}

      {/* ElevenLabs character usage — real usage numbers from the subscription
          endpoint. Voice synthesis fails outright when the quota hits zero. */}
      {elevenlabsStatus && elevenlabsStatus.status !== 'not_configured' && (() => {
        const s = elevenlabsStatus.status
        const isDown = s === 'quota_exhausted' || s === 'auth_error' || s === 'unavailable'
        const isWarn = s === 'quota_low'
        const tone = isDown
          ? { border: 'border-red-500/60', icon: 'text-red-500', text: 'text-red-600', label: s === 'quota_exhausted' ? 'Quota exhausted' : s === 'auth_error' ? 'Auth error' : 'Unavailable' }
          : isWarn
          ? { border: 'border-yellow-500/60', icon: 'text-yellow-500', text: 'text-yellow-600', label: 'Quota low' }
          : { border: '', icon: 'text-green-500', text: 'text-muted-foreground', label: 'Operational' }
        const hasUsage = typeof elevenlabsStatus.used === 'number' && typeof elevenlabsStatus.limit === 'number'
        const pct = elevenlabsStatus.pctUsed ?? 0
        const resetLabel = elevenlabsStatus.resetAt
          ? format(new Date(elevenlabsStatus.resetAt), 'MMM d')
          : null
        return (
          <Card className={tone.border}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div className="flex items-center gap-2">
                <Wallet className={`h-5 w-5 ${tone.icon}`} />
                <CardTitle className="text-base">ElevenLabs characters</CardTitle>
              </div>
              <div className="text-sm font-mono font-medium">
                {hasUsage
                  ? `${elevenlabsStatus.used!.toLocaleString()} / ${elevenlabsStatus.limit!.toLocaleString()}`
                  : tone.label}
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {hasUsage && (
                <Progress
                  value={pct}
                  className={`h-2 ${isDown ? '[&>div]:bg-red-500' : isWarn ? '[&>div]:bg-yellow-500' : ''}`}
                />
              )}
              <div className="flex items-center justify-between gap-4">
                <p className={`text-xs ${tone.text}`}>
                  {isDown
                    ? elevenlabsStatus.detail || 'Voice calls will fail until the quota resets or the plan is upgraded.'
                    : isWarn
                    ? `${pct}% used — consider upgrading before reset${resetLabel ? ` on ${resetLabel}` : ''}.`
                    : hasUsage
                    ? `${pct}% used${elevenlabsStatus.tier ? ` on ${elevenlabsStatus.tier}` : ''}${resetLabel ? ` · resets ${resetLabel}` : ''}.`
                    : 'Voice synthesis is running cleanly.'}
                </p>
                <div className="flex items-center gap-3 shrink-0">
                  {(isDown || isWarn) && (
                    <div className={`flex items-center gap-1 text-xs ${tone.text}`}>
                      <AlertTriangle className="h-3 w-3" />
                      <span>{tone.label}</span>
                    </div>
                  )}
                  <button
                    onClick={recheckElevenLabs}
                    disabled={elevenlabsRechecking}
                    className="text-xs underline text-muted-foreground hover:text-foreground whitespace-nowrap disabled:opacity-50"
                  >
                    {elevenlabsRechecking ? 'Checking…' : 'Recheck'}
                  </button>
                  <a
                    href="https://elevenlabs.io/app/subscription"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs underline text-muted-foreground hover:text-foreground whitespace-nowrap"
                  >
                    Manage plan
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })()}
      {elevenlabsStatus?.status === 'not_configured' && (
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base text-muted-foreground">ElevenLabs characters</CardTitle>
            </div>
            <div className="text-xs text-muted-foreground">
              ELEVENLABS_API_KEY not set
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Status legend — teaches a new super admin what each color means
          on the three API health cards above. Kept compact so it reads as a
          footer to the status row rather than its own section. */}
      <Card className="card-inset">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <span className="font-medium text-muted-foreground mr-1">Status key:</span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              <span className="font-medium text-foreground">Operational</span>
              <span className="text-muted-foreground">— normal</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              <span className="font-medium text-foreground">Quota exhausted</span>
              <span className="text-muted-foreground">— out of credit, top up</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              <span className="font-medium text-foreground">Auth error</span>
              <span className="text-muted-foreground">— bad API key</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-yellow-500" />
              <span className="font-medium text-foreground">Rate limited</span>
              <span className="text-muted-foreground">— transient, self-heals</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-yellow-500" />
              <span className="font-medium text-foreground">Unavailable</span>
              <span className="text-muted-foreground">— network / upstream issue</span>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Stripe backfill — super-admin-only tool to heal zombie orgs. Sits
          below the API health cards so it doesn't crowd the status at the
          top of the dashboard. */}
      {isSuperAdmin && <StripeBackfillCard />}

      {/* Live Call Activity — platform-wide concurrent calls */}
      {concurrency && (
        <Card className={concurrency.utilization >= 90 ? 'border-red-500/50' : concurrency.utilization >= 70 ? 'border-yellow-500/50' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <Activity className={`h-5 w-5 ${
                concurrency.utilization >= 90 ? 'text-red-500' :
                concurrency.utilization >= 70 ? 'text-yellow-500' : 'text-green-500'
              }`} />
              <CardTitle className="text-base">Live Call Activity (platform-wide)</CardTitle>
            </div>
            <div className="text-sm font-mono font-medium flex items-center gap-2">
              {concurrencyStale && (
                <span className="text-[10px] font-sans font-normal px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                  Stale — live count unavailable
                </span>
              )}
              {concurrency.activeCount} / {concurrency.effectiveLimit}
              {concurrency.queueDepth > 0 && (
                <span className="text-muted-foreground font-normal ml-3">
                  +{concurrency.queueDepth} queued
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-colors duration-500 ${
                  concurrency.utilization >= 90 ? 'bg-red-500' :
                  concurrency.utilization >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(concurrency.utilization, 100)}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-muted-foreground">
                {concurrency.utilization >= 90
                  ? 'Near capacity — new callers will leave voicemail'
                  : concurrency.utilization >= 70
                  ? 'Moderate usage — monitor for spikes'
                  : `${concurrency.utilization}% of ${concurrency.effectiveLimit} concurrent call capacity`}
              </p>
              {concurrency.utilization >= 90 && (
                <div className="flex items-center gap-1 text-xs text-red-600">
                  <AlertTriangle className="h-3 w-3" />
                  <span>Capacity alert</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top KPI Row - 5 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organizations</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.organizations.total}</div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">
                {analytics.organizations.active} active
              </p>
              {formatGrowth(analytics.organizations.growthRate)}
            </div>
          </CardContent>
        </Card>

        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.agents.total}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.agents.online} online now
            </p>
          </CardContent>
        </Card>

        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversations</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.conversations.thisMonth.toLocaleString()}</div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">this month</p>
              {formatGrowth(analytics.conversations.growthRate)}
            </div>
          </CardContent>
        </Card>

        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Voice Cost</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(analytics.costs.voiceCostThisMonth)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.voiceCalls.thisMonth} calls this month
            </p>
          </CardContent>
        </Card>

        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Phone Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(analytics.costs.phoneCostThisMonth)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              monthly recurring
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversations Trend */}
        <Card className="card-inset">
          <CardHeader>
            <CardTitle>Conversation Trend</CardTitle>
            <CardDescription>Daily conversations (30 days)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={conversationTrendFormatted}>
                <defs>
                  <linearGradient id="colorConv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(208 100% 50%)" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="hsl(208 100% 50%)" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="displayDate"
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="hsl(208 100% 50%)"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorConv)"
                  name="Conversations"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Voice Cost Trend */}
        <CostTrendChart data={analytics.voiceTrend} />
      </div>

      {/* Cost Breakdown + Subscription Tier */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CostBreakdownChart data={analytics.costs.breakdown} />

        {/* Subscription Distribution */}
        <Card className="card-inset">
          <CardHeader>
            <CardTitle>Subscription Distribution</CardTitle>
            <CardDescription>Organizations by subscription tier</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(analytics.organizations.byTier).map(([tier, count]) => {
                const total = analytics.organizations.total || 1
                const percentage = Math.round((count / total) * 100)
                return (
                  <div key={tier}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium capitalize">{tier}</span>
                      <span className="text-sm text-muted-foreground">
                        {count} ({percentage}%)
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Organizations with Cost */}
        <Card className="card-inset">
          <CardHeader>
            <CardTitle>Top Organizations</CardTitle>
            <CardDescription>By activity this month</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead className="text-right">Messages</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.topOrganizations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <Link to={`/admin/organizations/${org.id}`} className="hover:underline">
                        <p className="font-medium">{org.name}</p>
                        <p className="text-xs text-muted-foreground">{org.slug}</p>
                      </Link>
                    </TableCell>
                    <TableCell>{getTierBadge(org.subscriptionTier)}</TableCell>
                    <TableCell className="text-right">{org.messageCount.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {org.voiceCost > 0 ? formatCurrency(org.voiceCost) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {analytics.topOrganizations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Recent Signups */}
        <Card className="card-inset">
          <CardHeader>
            <CardTitle>Recent Signups</CardTitle>
            <CardDescription>Latest organization registrations</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.recentSignups.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <Link to={`/admin/organizations/${org.id}`} className="hover:underline">
                        <p className="font-medium">{org.name}</p>
                        <p className="text-xs text-muted-foreground">{org.ownerEmail}</p>
                      </Link>
                    </TableCell>
                    <TableCell>{getStatusBadge(org.status)}</TableCell>
                    <TableCell className="text-right text-sm">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
                {analytics.recentSignups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      No recent signups
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Message Distribution */}
      <Card className="card-inset">
        <CardHeader>
          <CardTitle>Message Distribution</CardTitle>
          <CardDescription>By sender type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600">
                {analytics.messages.byType.visitor.toLocaleString()}
              </div>
              <p className="text-sm text-muted-foreground">Visitor Messages</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600">
                {analytics.messages.byType.ai.toLocaleString()}
              </div>
              <p className="text-sm text-muted-foreground">AI Responses</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600">
                {analytics.messages.byType.agent.toLocaleString()}
              </div>
              <p className="text-sm text-muted-foreground">Agent Messages</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
